import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Form from "react-bootstrap/Form";
import {
    Breadcrumb,
    Button,
    Col,
    Row,
    Spinner,
} from "react-bootstrap";
import moment from "moment";
import "moment/locale/sv";
import { getOrderStatusById } from "../../app/services/orderStatusService";
import { ClientType } from "../../models/enums/clientTypeEnum";

interface Props { }

const booleanLabel = (value: boolean) => (value ? "Ja" : "Nej");

const clientTypeLabel = (types: ClientType[] | null | undefined) => {
    if (!types || types.length === 0) return "Alla";
    const labels = types.map(type => {
        switch (type) {
            case ClientType.Standard:
                return "Standard";
            case ClientType.Catalog:
                return "Katalog";
            case ClientType.Fleet:
                return "Fleet";
            default:
                return "";
        }
    });
    return labels.join(", ");
};

const OrderStatusView: FC<Props> = () => {
    moment.locale("sv");
    const { orderStatusId } = useParams<{ orderStatusId: string }>();
    const dispatch = useAppDispatch();

    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const orderStatusState = useSelector((state: RootState) => state.orderStatus);
    const orderStatus = orderStatusState.orderStatus;
    const loading = orderStatusState.isWaitingOrderStatusGetResponse;

    useEffect(() => {
        dispatch(getOrderStatusById(instance, account, orderStatusId));
    }, [account, accounts, dispatch, instance, orderStatusId]);

    if (loading) {
        return (
            <Row className="mb-3">
                <Col className="text-center">
                    <Spinner variant="primary" />
                </Col>
            </Row>
        );
    }

    return (
        <div>
            <Breadcrumb>
                <Breadcrumb.Item href="/admin/order-status">Orderstatusar</Breadcrumb.Item>
                <Breadcrumb.Item active>{orderStatus.name}</Breadcrumb.Item>
            </Breadcrumb>
            <hr />
            <Form>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatus.name}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Sortering</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatus.sortOrder ?? ""}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Klient-typ</Form.Label>
                            <Form.Control
                                type="text"
                                value={clientTypeLabel(orderStatus.clientTypes)}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Skapad</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatus.createdAt ? moment(orderStatus.createdAt).format("YYYY-MM-DD HH:mm") : ""}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Uppdaterad</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatus.updatedAt ? moment(orderStatus.updatedAt).format("YYYY-MM-DD HH:mm") : ""}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Borttagen</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatus.deletedAt ? moment(orderStatus.deletedAt).format("YYYY-MM-DD HH:mm") : ""}
                                readOnly
                                disabled
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Visa i meny</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="showInMenu"
                                    label={booleanLabel(orderStatus.showInMenu)}
                                    checked={orderStatus.showInMenu}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>ableToExportOrdersInStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="ableToExportOrdersInStatus"
                                    label={booleanLabel(orderStatus.ableToExportOrdersInStatus)}
                                    checked={orderStatus.ableToExportOrdersInStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>aXCallbackStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="axCallbackStatus"
                                    label={booleanLabel(orderStatus.axCallbackStatus)}
                                    checked={orderStatus.axCallbackStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>requiresCatalogApproval</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="requiresCatalogApproval"
                                    label={booleanLabel(orderStatus.requiresCatalogApproval)}
                                    checked={orderStatus.requiresCatalogApproval}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>catalogCallbackStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="catalogCallbackStatus"
                                    label={booleanLabel(orderStatus.catalogCallbackStatus)}
                                    checked={orderStatus.catalogCallbackStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>catalogDeniedStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="catalogDeniedStatus"
                                    label={booleanLabel(orderStatus.catalogDeniedStatus)}
                                    checked={orderStatus.catalogDeniedStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>orderFinalized</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="orderFinalized"
                                    label={booleanLabel(orderStatus.orderFinalized)}
                                    checked={orderStatus.orderFinalized}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                 <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>deliveredStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="deliveredStatus"
                                    label={booleanLabel(orderStatus.deliveredStatus)}
                                    checked={orderStatus.deliveredStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                       <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>goodsInWarehouseStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="goodsInWarehouseStatus"
                                    label={booleanLabel(orderStatus.goodsInWarehouseStatus)}
                                    checked={orderStatus.goodsInWarehouseStatus}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

            </Form>
            {orderStatus.orderPageType && (
                <>
                    <h4 className="mt-4">OrderPageType</h4>
                    <Form>
                        <Row>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={orderStatus.orderPageType.id}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Skapad</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={orderStatus.orderPageType.createdAt ? moment(orderStatus.orderPageType.createdAt).format("YYYY-MM-DD HH:mm") : ""}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Uppdaterad</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={orderStatus.orderPageType.updatedAt ? moment(orderStatus.orderPageType.updatedAt).format("YYYY-MM-DD HH:mm") : ""}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Borttagen</Form.Label>
                                    <Form.Control
                                        type="text"
                                         value={orderStatus.orderPageType.deletedAt ? moment(orderStatus.orderPageType.deletedAt).format("YYYY-MM-DD HH:mm") : ""}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Namn</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={orderStatus.orderPageType.name}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Beskrivning</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={orderStatus.orderPageType.description ?? ""}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            {[
                                "customer", "assignedTo", "registrationNumber", "vin", "carInfo", "aoNumber", "externalOrderId", "foNumber", "ioNumber", "orderType",
                                "orderedBy", "followUp", "emailBody", "warehouseLocation", "supplier", "arriveDate", "bookingDate", "deliveryDate", "pickupTime",
                                "orderDate", "fileName", "comment", "internalComment", "carUrl",
                                "articleTableQuantity", "articleTableDescription", "articleTableArticleNumber", "articleTablePurchasePrice", "articleTableSalesPrice",
                                "articleTableSupplierName", "articleTableIONumber", "articleTableComment", "articleTableDiscount", "articleTablePartUrl", "articleTableIsSyncedWithAX",
                                "emailPriceProposal", "emailVerification"
                            ].map((field) => (
                                <Col xs={12} md={4} key={field}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{field}</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="switch"
                                                id={field}
                                                label={booleanLabel((orderStatus.orderPageType as any)[field])}
                                                checked={(orderStatus.orderPageType as any)[field]}
                                                readOnly
                                                disabled
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                    </Form>
                </>
            )}

            <Row>
                <div className="text-end">
                    <Button variant="primary" href={`/admin/order-status/edit/${orderStatus.id}`}>
                        Redigera
                    </Button>
                </div>
            </Row>
        </div>
    );
};

export default OrderStatusView;